import { apiClient } from './api';

export type MMStatus = {
  configured: boolean;
  ok: boolean;
  username?: string;
  userId?: string;
  baseUrl?: string;
  error?: string;
  fetchedAt: string;
};

export type MMTeam = {
  id: string;
  name: string;
  display_name?: string;
  displayName?: string;
  type?: string;
};

export type MMChannel = {
  id: string;
  teamId: string;
  teamName?: string;
  type: string;
  name: string;
  displayName: string;
  lastPostAt?: string;
  messageCount?: number;
  header?: string;
  purpose?: string;
};

export type MMAttachment = {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  extension?: string;
  createAt?: string;
  downloadAvailable: boolean;
};

export type MMMessage = {
  id: string;
  channelId: string;
  channelName?: string;
  channelDisplayName?: string;
  teamId?: string;
  userId: string;
  username: string;
  displayName: string;
  message: string;
  createAt: string;
  updateAt?: string;
  edited: boolean;
  rootId?: string;
  isReply: boolean;
  replyCount: number;
  hasAttachments: boolean;
  attachments?: MMAttachment[];
  reactionsCount: number;
  reactions?: { emoji: string; userId?: string }[];
  permalink?: string;
};

export type MMThread = {
  root?: MMMessage;
  parent?: MMMessage;
  replies: MMMessage[];
};

function unwrap<T>(res: { data?: T } | T): T {
  if (res && typeof res === 'object' && 'data' in (res as object) && (res as { data?: T }).data !== undefined) {
    return (res as { data: T }).data;
  }
  return res as T;
}

export const mattermostService = {
  async status() {
    return unwrap(await apiClient.get<MMStatus>('/admin/mattermost/status'));
  },
  async teams() {
    const raw = unwrap(await apiClient.get<MMTeam[] | { data: MMTeam[] }>('/admin/mattermost/teams'));
    return (Array.isArray(raw) ? raw : []) as MMTeam[];
  },
  async channels(teamId?: string) {
    const q = teamId ? `?teamId=${encodeURIComponent(teamId)}` : '';
    const raw = unwrap(await apiClient.get<MMChannel[]>(`/admin/mattermost/channels${q}`));
    return (Array.isArray(raw) ? raw : []) as MMChannel[];
  },
  async messages(params: {
    channelId: string;
    page?: number;
    perPage?: number;
    before?: string;
    after?: string;
  }) {
    const sp = new URLSearchParams();
    sp.set('channelId', params.channelId);
    if (params.page != null) sp.set('page', String(params.page));
    if (params.perPage != null) sp.set('perPage', String(params.perPage));
    if (params.before) sp.set('before', params.before);
    if (params.after) sp.set('after', params.after);
    return unwrap(
      await apiClient.get<{
        data: MMMessage[];
        page: number;
        perPage: number;
        hasNext: boolean;
        channel?: MMChannel;
        fetchedAt?: string;
      }>(`/admin/mattermost/messages?${sp}`)
    );
  },
  async search(params: {
    q: string;
    teamId?: string;
    channelId?: string;
    from?: string;
    after?: string;
    before?: string;
    page?: number;
    perPage?: number;
  }) {
    const sp = new URLSearchParams();
    sp.set('q', params.q);
    if (params.teamId) sp.set('teamId', params.teamId);
    if (params.channelId) sp.set('channelId', params.channelId);
    if (params.from) sp.set('from', params.from);
    if (params.after) sp.set('after', params.after);
    if (params.before) sp.set('before', params.before);
    if (params.page != null) sp.set('page', String(params.page));
    if (params.perPage != null) sp.set('perPage', String(params.perPage));
    return unwrap(
      await apiClient.get<{ data: MMMessage[]; page: number; perPage: number; terms?: string; fetchedAt?: string }>(
        `/admin/mattermost/search?${sp}`
      )
    );
  },
  async message(id: string) {
    return unwrap(await apiClient.get<MMMessage>(`/admin/mattermost/messages/${id}`));
  },
  async thread(id: string) {
    return unwrap(await apiClient.get<MMThread>(`/admin/mattermost/messages/${id}/thread`));
  },
  fileDownloadUrl(id: string) {
    const base = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';
    return `${base}/admin/mattermost/files/${encodeURIComponent(id)}/download`;
  },
};
